import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserAvatar } from './UserAvatar';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Icons
import { MessageSquare, Send, Inbox, Edit, Users, Star, Archive, Reply } from 'lucide-react';

const ROLE_LABELS = { ADMIN: 'Administrateur', PRINCIPAL: 'Directeur', TEACHER: 'Professeur', PARENT: 'Parent', STUDENT: 'Élève', TREASURER: 'Trésorier', ACCOUNTANT: 'Comptable', SECRETARY: 'Secrétaire', SUPER_ADMIN: 'Super Admin', LIFE_COUNSELOR: 'Vie scolaire' };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagingSystem = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inbox');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  
  const [replyTo, setReplyTo] = useState(null);
  const [counts, setCounts] = useState({ inbox: 0, unread: 0, sent: 0, urgent: 0 });

  const fetchCounts = async () => {
    try {
      const [inbox, sent] = await Promise.all([axios.get(`${API}/messages?folder=inbox`), axios.get(`${API}/messages?folder=sent`)]);
      setCounts({ inbox: inbox.data.length, unread: inbox.data.filter((m) => !m.read).length, sent: sent.data.length, urgent: inbox.data.filter((m) => m.is_urgent).length });
    } catch (e) {
      console.error('Error fetching counts', e);
    }
  };
  const [messageForm, setMessageForm] = useState({
    recipient_ids: [],
    cc_ids: [],
    subject: '',
    content: '',
    priority: 'NORMAL',
    is_urgent: false
  });

  const priorities = [
    { value: 'LOW', label: 'Faible', color: 'badge-secondary' },
    { value: 'NORMAL', label: 'Normal', color: 'badge-primary' },
    { value: 'HIGH', label: 'Élevé', color: 'badge-warning' },
    { value: 'URGENT', label: 'Urgent', color: 'badge-danger' }
  ];

  useEffect(() => {
    fetchMessages();
    fetchUsers();
  }, [activeTab]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const folder = activeTab === 'sent' ? 'sent' : 'inbox';
      const response = await axios.get(`${API}/messages?folder=${folder}`);
      setMessages(response.data);
      fetchCounts();
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/messages/available-recipients`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des destinataires');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.recipient_ids.length) {
      toast.error('Choisissez un destinataire');
      return;
    }
    setLoading(true);
    
    try {
      await axios.post(`${API}/messages`, messageForm);
      
      toast.success('Message envoyé avec succès');
      resetForm();
      setDialogOpen(false);
      fetchMessages(); // Recharger les messages
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du message');
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setReplyTo(null);
    setMessageForm({
      recipient_ids: [],
      cc_ids: [],
      subject: '',
      content: '',
      priority: 'NORMAL',
      is_urgent: false
    });
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.post(`${API}/messages/${messageId}/read`);
      fetchMessages(); // Recharger les messages
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleReply = (message) => {
    const to = message.sender || (message.recipients || [])[0];
    if (!to) return;
    setReplyTo(to);
    setMessageForm({
      recipient_ids: [to.id],
      cc_ids: (message.cc_ids || []).filter((id) => id !== user?.id && id !== to.id),
      subject: message.subject?.startsWith('Re:') ? message.subject : `Re: ${message.subject}`,
      content: '',
      priority: 'NORMAL',
      is_urgent: false,
      parent_message_id: message.id
    });
    setDialogOpen(true);
  };

  const ccCandidates = users.filter((u) => ['TREASURER', 'PRINCIPAL', 'ADMIN'].includes(u.role) && !messageForm.recipient_ids.includes(u.id));
  const toggleCc = (id) => setMessageForm((f) => ({
    ...f, cc_ids: f.cc_ids.includes(id) ? f.cc_ids.filter((x) => x !== id) : [...f.cc_ids, id]
  }));

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    if (!message.read && activeTab === 'inbox') {
      await handleMarkAsRead(message.id);
    }
  };

  const markAsRead = (messageId) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      )
    );
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : 'Utilisateur inconnu';
  };

  const getSenderName = (message) => {
    // Si sender est un objet avec first_name et last_name
    if (message.sender && message.sender.first_name) {
      return `${message.sender.first_name} ${message.sender.last_name}`;
    }
    // Si sender_id existe, utiliser getUserName
    if (message.sender_id) {
      return getUserName(message.sender_id);
    }
    // Fallback
    return 'Expéditeur inconnu';
  };

  const getPriorityInfo = (priority) => {
    return priorities.find(p => p.value === priority) || priorities[1];
  };

  const getFilteredMessages = () => {
    // L'API retourne déjà les messages filtrés par folder
    if (activeTab === 'unread') {
      return messages.filter(msg => !msg.read);
    }
    return messages;
  };

  if (loading && messages.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in" data-testid="messaging-system">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <MessageSquare className="w-6 h-6 mr-2 text-blue-600" />
            Messagerie
          </h1>
          <p className="text-gray-600 mt-1">
            Communiquez avec l'équipe éducative et les parents
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="compose-message-btn"
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="btn-primary"
            >
              <Edit className="w-4 h-4 mr-2" />
              Nouveau Message
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nouveau message</DialogTitle>
              <DialogDescription>
                Envoyez un message à un ou plusieurs destinataires
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recipients">Destinataire</Label>
                {replyTo ? (
                  <div className="px-3 py-2 border rounded-md bg-gray-50 text-sm" data-testid="reply-recipient">
                    Réponse à {replyTo.first_name} {replyTo.last_name}
                  </div>
                ) : (
                <Select 
                  value={messageForm.recipient_ids[0] || ''} 
                  onValueChange={(value) => setMessageForm({ ...messageForm, recipient_ids: [value], cc_ids: messageForm.cc_ids.filter((c) => c !== value) })}
                >
                  <SelectTrigger data-testid="message-recipients-select">
                    <SelectValue placeholder={users.length ? 'Sélectionnez un destinataire' : 'Aucun destinataire disponible'} />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          {u.first_name} {u.last_name} ({ROLE_LABELS[u.role] || u.role})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
              </div>

              {ccCandidates.length > 0 && (
                <div className="space-y-2" data-testid="message-cc-section">
                  <Label>Mettre en copie</Label>
                  <div className="flex flex-wrap gap-3">
                    {ccCandidates.map((u) => (
                      <label key={u.id} className="flex items-center space-x-2 text-sm border rounded-md px-2 py-1 cursor-pointer">
                        <input type="checkbox" data-testid={`cc-${u.id}`} checked={messageForm.cc_ids.includes(u.id)} onChange={() => toggleCc(u.id)} />
                        <span>{u.first_name} {u.last_name} ({ROLE_LABELS[u.role] || u.role})</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="priority">Priorité</Label>
                  <Select 
                    value={messageForm.priority} 
                    onValueChange={(value) => setMessageForm({ ...messageForm, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorities.map((priority) => (
                        <SelectItem key={priority.value} value={priority.value}>
                          {priority.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2 flex items-end">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_urgent"
                      checked={messageForm.is_urgent}
                      onChange={(e) => setMessageForm({ ...messageForm, is_urgent: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="is_urgent">Message urgent</Label>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">Objet</Label>
                <Input
                  id="subject"
                  data-testid="message-subject-input"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  placeholder="Objet du message"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="content">Message</Label>
                <Textarea
                  id="content"
                  data-testid="message-content-input"
                  value={messageForm.content}
                  onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                  placeholder="Tapez votre message ici..."
                  rows={6}
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="btn-primary"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? 'Envoi...' : 'Envoyer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Messages Reçus</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              <span data-testid="msg-count-inbox">{counts.inbox}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Total reçus</p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Non Lus</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              <span data-testid="msg-count-unread">{counts.unread}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Messages non lus</p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Envoyés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              <span data-testid="msg-count-sent">{counts.sent}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Messages envoyés</p>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Urgents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              <span data-testid="msg-count-urgent">{counts.urgent}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Messages urgents</p>
          </CardContent>
        </Card>
      </div>

      {/* Message Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="inbox" className="flex items-center">
            <Inbox className="w-4 h-4 mr-2" />
            Boîte de réception
          </TabsTrigger>
          <TabsTrigger value="unread" className="flex items-center">
            <Star className="w-4 h-4 mr-2" />
            Non lus
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center">
            <Send className="w-4 h-4 mr-2" />
            Envoyés
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="space-y-4">
          {/* Message List */}
          {getFilteredMessages().length > 0 ? (
            <div className="space-y-3">
              {getFilteredMessages().map((message) => {
                const priorityInfo = getPriorityInfo(message.priority);
                
                return (
                  <Card 
                    key={message.id} 
                    className={`card cursor-pointer transition-all hover:shadow-md ${!message.read ? 'border-l-4 border-l-blue-500 bg-blue-50' : ''}`}
                    onClick={() => handleViewMessage(message)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className={`font-semibold ${!message.read ? 'text-blue-900' : 'text-gray-900'}`}>
                              {message.subject}
                            </h3>
                            {!message.read && (
                              <Badge className="badge-primary text-xs">Nouveau</Badge>
                            )}
                            {message.is_urgent && (
                              <Badge className="badge-danger text-xs">Urgent</Badge>
                            )}
                            <Badge className={`${priorityInfo.color} text-xs`}>
                              {priorityInfo.label}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                            <UserAvatar size="xs" person={activeTab === 'sent' ? message.recipients?.[0] : message.sender} testId={`message-avatar-${message.id}`} />
                            {activeTab === 'sent' 
                              ? `À: ${message.recipients && Array.isArray(message.recipients) ? message.recipients.map((r) => `${r.first_name} ${r.last_name}`).join(', ') : 'Aucun destinataire'}`
                              : `De: ${getSenderName(message)}`
                            }
                          </p>
                          
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {message.content}
                          </p>
                          
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-gray-500">
                              {new Date(message.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            
                            {activeTab !== 'sent' && message.sender && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReply(message);
                                }}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Reply className="w-4 h-4 mr-1" />
                                Répondre
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun message
              </h3>
              <p className="text-gray-600">
                {activeTab === 'inbox' && 'Vous n\'avez reçu aucun message.'}
                {activeTab === 'sent' && 'Vous n\'avez envoyé aucun message.'}
                {activeTab === 'unread' && 'Tous vos messages sont lus.'}
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{selectedMessage.subject}</span>
                <div className="flex space-x-2">
                  {selectedMessage.is_urgent && (
                    <Badge className="badge-danger">Urgent</Badge>
                  )}
                  <Badge className={`${getPriorityInfo(selectedMessage.priority).color}`}>
                    {getPriorityInfo(selectedMessage.priority).label}
                  </Badge>
                </div>
              </DialogTitle>
              <DialogDescription>
                <UserAvatar size="xs" className="inline-flex align-middle mr-1" person={selectedMessage.sender} />
                De: {getSenderName(selectedMessage)} • 
                {new Date(selectedMessage.sent_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </DialogDescription>
            </DialogHeader>
            
            <div className="mt-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  data-testid="message-detail-reply-btn"
                  onClick={() => {
                    const m = selectedMessage;
                    setSelectedMessage(null);
                    handleReply(m);
                  }}
                >
                  <Reply className="w-4 h-4 mr-2" />
                  Répondre
                </Button>
                <Button onClick={() => setSelectedMessage(null)}>
                  Fermer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MessagingSystem;